import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AuthenticatorData } from '../api/auth';
import { Logger } from '../monitoring';
import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';

interface MfaData {
  mfaToken?: string;
  authenticators?: AuthenticatorData[];
  challengedAuthenticators?: string[];
}

const MFA_DATA_KEY = 'mfa_data';

// Fallback for when sessionStorage itself throws (blocked storage, sandboxed iframes, some
// in-app browser WebViews). Doesn't survive a page refresh — only bridges the SPA navigation
// from Login/SignUp to the MFA challenge page, which is all the happy path needs.
let inMemoryMfaData: string | null = null;

/**
 * Reads the MFA data saved by navigateToMfaChallenge, falling back to the in-memory copy if
 * sessionStorage was unavailable when it was saved.
 */
export const readStoredMfaData = (): string | null => {
  return getSessionStorageItemSafely(MFA_DATA_KEY) ?? inMemoryMfaData;
};

export const clearStoredMfaData = (): void => {
  removeSessionStorageItemSafely(MFA_DATA_KEY);
  inMemoryMfaData = null;
};

/**
 * Hook that provides a function to save MFA data and navigate to the MFA challenge page.
 */
export const useMfaNavigation = () => {
  const navigate = useNavigate();

  const navigateToMfaChallenge = useCallback(
    (mfaData: MfaData) => {
      const serialized = JSON.stringify({
        mfaToken: mfaData.mfaToken,
        authenticators: mfaData.authenticators,
        challengedAuthenticators: mfaData.challengedAuthenticators,
      });

      if (!setSessionStorageItemSafely(MFA_DATA_KEY, serialized)) {
        Logger.warn('sessionStorage unavailable for MFA data, falling back to in-memory storage');
        inMemoryMfaData = serialized;
      }

      navigate('/mfa/challenge');
    },
    [navigate]
  );

  return { navigateToMfaChallenge };
};
